import { Flow, Term } from './types'

// Non-price marketing copy only. All prices, seat counts and dates come from the list-prices API.

// The 12-Week Sprint plan is HIDDEN for now. Set this to true to bring it back — that restores the
// Annual/Sprint toggle, the sprint price fetch, and the "or try the 12-Week Sprint" note. All the
// sprint code and copy below is kept intact on purpose.
export const SPRINT_ENABLED = false

// Fluency Intelligence has not shipped yet, so every mention of it carries a "Coming soon" chip.
// Remove this once the feature is live — the copy itself needs no change.
export const COMING_SOON_FEATURE = 'Fluency Intelligence'

export function isComingSoon(feature: string): boolean {
  return feature.includes(COMING_SOON_FEATURE)
}

export const TERM_LABELS: Record<Term, string> = {
  annual: 'Annual',
  sprint: '12-Week Sprint',
}

// Per-term copy overrides (only teacher has multiple terms today).
interface TermOverride {
  priceUnit?: string // overrides the flow's default price unit
  subtitle?: string // small grey line under the price
  note?: string // emphasised italic note under the subtitle
}

interface FlowConfig {
  // Page header
  heading: string // e.g. "Upgrade your plan"
  freeNoun: string // "Free {noun}" in the status pill, e.g. "Free Teacher"
  // Plan card
  planName: string
  priceUnit: string // e.g. "per teacher · year"
  // Purchase-details labels
  includedLabel: string // e.g. "Students included"
  extraLabel: string // e.g. "Extra seats"
  extraUnit: string // e.g. "student · year"
  billingLabel: string
  // Whether this flow bills purely per seat (school) vs base + extras (teacher/family)
  perSeat: boolean
  minSeats?: number
  // Static marketing bullets (the dynamic seat/price bullet is prepended at render time)
  features: string[]
  // Whether the annual/sprint term toggle is shown
  hasTermToggle: boolean
  // Whether the Pro card shows the gold "RECOMMENDED" badge (it always keeps the gold border)
  showRecommendedBadge: boolean
  // Whether the seat bullet appends the extra-seat price (teacher) or is just "{n} included" (family)
  seatBulletShowsExtraPrice: boolean
  // Copy that differs by selected term
  terms?: Partial<Record<Term, TermOverride>>
}

export const FLOW_CONFIG: Record<Flow, FlowConfig> = {
  teacher: {
    heading: 'Upgrade your plan',
    freeNoun: 'Teacher',
    planName: 'Teacher Pro',
    priceUnit: 'per teacher · year',
    includedLabel: 'Students included',
    extraLabel: 'Extra seats',
    extraUnit: 'student · year',
    billingLabel: 'Annual',
    perSeat: false,
    features: [
      'Fluency Intelligence: Classroom Brief',
      'Fluency Intelligence: Student Brief',
      'Unlimited Student Programs',
      'Custom Program Settings',
      'Advanced Reports',
      'Priority Technical Support',
    ],
    // Sprint is hidden while SPRINT_ENABLED is false. This also skips the sprint price fetch and
    // the "or try the 12-Week Sprint" note, so only the Annual plan is shown.
    hasTermToggle: SPRINT_ENABLED,
    showRecommendedBadge: true,
    seatBulletShowsExtraPrice: true,
    terms: {
      sprint: {
        priceUnit: 'one trimester · 12 weeks',
        note: 'Roll into the annual plan any time.',
      },
    },
  },
  family: {
    heading: 'Upgrade your family plan',
    freeNoun: 'Parent',
    planName: 'Family Pro',
    priceUnit: '/ year',
    includedLabel: 'Children included',
    extraLabel: 'Extra children',
    extraUnit: 'child · year',
    billingLabel: 'Annual',
    perSeat: false,
    features: [
      'Fluency Intelligence: Student Brief',
      'Unlimited Student Programs',
      'Custom Program Settings',
      'Advanced Reports',
      'Priority Technical Support',
    ],
    hasTermToggle: false,
    showRecommendedBadge: false,
    seatBulletShowsExtraPrice: false,
  },
  school: {
    heading: 'Upgrade your school plan',
    freeNoun: 'School',
    planName: 'School Pro',
    // A school is quoted one total for its roster, not a per-seat rate.
    priceUnit: 'per year',
    includedLabel: 'Students',
    extraLabel: 'Number of students',
    extraUnit: 'student · year',
    billingLabel: 'Annual',
    perSeat: true,
    minSeats: 125,
    features: [
      'Fluency Intelligence: School Brief for the principal',
      'Fluency Intelligence: Classroom Brief',
      'Fluency Intelligence: Student Brief',
      'Unlimited Student Programs',
      'Custom Program Settings',
      'Advanced Reports',
      'Priority Technical Support',
    ],
    hasTermToggle: false,
    showRecommendedBadge: true,
    seatBulletShowsExtraPrice: true,
  },
}

// Display name for a license type — used by the status pill (e.g. "Teacher Premium expired").
export const LICENSE_TYPE_NAMES: Record<string, string> = {
  classroom: 'Teacher Premium',
  classroompro: 'Teacher Pro',
  homeschool: 'Family Premium',
  homeschoolpro: 'Family Pro',
  school: 'School Premium',
  schoolpro: 'School Pro',
  district: 'District',
}

// Legacy Premium plan copy, shown in a secondary (non-recommended) box below Pro. Only teacher &
// family offer Premium self-serve; school routes to sales. The seat line is just "{n} included"
// (no extra-seat suffix), matching the Premium card design.
interface PremiumConfig {
  planName: string
  priceUnit: string
  // Small grey chip in the card corner, e.g. "Legacy".
  chip?: string
  // Plans with an included allowance (teacher: 50 students) show an "{n} included" bullet.
  // Per-child plans (family) price every child, so there is no allowance to state.
  showSeatBullet: boolean
  features: string[]
}

export const PREMIUM_CONFIG: Partial<Record<Flow, PremiumConfig>> = {
  teacher: {
    planName: 'Teacher Premium',
    priceUnit: 'per teacher · year',
    chip: 'Legacy',
    showSeatBullet: true,
    features: [
      'Unlimited Student Programs',
      'Unlimited Student Programs',
      'Custom Program Settings',
      'Advanced Reports',
      'Priority Technical Support',
    ],
  },
  family: {
    planName: 'Family Premium',
    priceUnit: 'per child · year',
    chip: 'Legacy',
    showSeatBullet: false,
    features: [
      'Unlimited Student Programs',
      'Printable Student Activities',
      'Priority Technical Support',
    ],
  },
  school: {
    planName: 'School Premium',
    priceUnit: 'per school · year',
    chip: 'Legacy',
    // The legacy school plan covers the whole school, so it states that instead of a seat count.
    showSeatBullet: false,
    features: [
      'Unlimited students included',
      'Unlimited Student Programs',
      'Unlimited Student Programs',
      'Custom Program Settings',
      'Advanced Reports',
      'Priority Technical Support',
    ],
  },
}

// NOTE: the sales email and the HubSpot demo/quote links are written inline in OtherOptions.tsx
// and CustomPlanPanel.tsx.
